import License, { LicenseStatus, SpectralService } from "spectral-core/models/License";

import React, { useMemo, useState } from "react";
import { 
    Avatar,
    Button, 
    Card, 
    CardActions, 
    CardContent, 
    CardHeader, 
    Chip, 
    Link, 
    makeStyles,
    Tooltip } from "@material-ui/core";

import { GetAppRounded, Info } from "@material-ui/icons";

import { DesktopMac, PhotoCamera, Smartphone, Edit, Replay } from "@material-ui/icons";
import Warning from "components/Typography/Warning";
import { updateLicense } from "spectral-core/api";
import LicenseUpdateDTO from "spectral-core/dtos/LicenseUpdateDTO";
import LicenseDialog from "components/Dialogs/LicenseDialog";
import { useRootSelector } from "hooks/reduxHooks";
import RenewLicenseDialog from "components/Dialogs/RenewLicenseDialog";
import intl from "react-intl-universal";
import LicenseDetailsDialog from "components/Dialogs/LicenseDetailsDialog";

interface CardLicenseProps {
    license: License;
    onSave: () => void;
    showSnackBar: (message: string, isSuccess: boolean) => void;
};

export interface LicenseForm {
  name: string;
  description: string;
  price: number;
  expiresAt: Date;
  service: number;
  users: string[];
  isDeviceRestrict: boolean;
  devices: string;
  isModelRestrict: boolean;
  models: string[];
  computerMac: string;
};

const useStyles = makeStyles((theme) => ({
    invalid: {  
        backgroundColor: "gray",
        borderColor: "gray",
        color: "black"
    },
    valid: {
        backgroundColor: "#33c481",
        borderColor: "#33c481",
        color: "white"
    },
    expired: {
        backgroundColor: "#e08b8b",
        borderColor: "#e08b8b",
        color: "black"
    },
    expiringSoon: {
        backgroundColor: "#f8eb75",
        borderColocar: "#f8eb75",
        color: "black"
    },
    card: {
        maxWidth: 370,
        height: 230,
        margin: 2, 
        borderRadius: 12, 
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        transition: "transform 0.2s",
    },
    avatar: {
        background: "linear-gradient(60deg, #02adc2, #00acc1)",
        color: "white"
    },
    description: {
        display: "--webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        textOverflow: "ellipsis",
        wordBreak: "break-word"
    }
}));

const toLicenseUpdateDTO = (license: LicenseForm): LicenseUpdateDTO => {
    const devicesFormated = license.devices.length > 1 
        ? license.devices.split(",").map(d => d.trim()) 
        : license.devices

    return {
        name: license.name,
        description: license.description,
        price: license.price,
        expiresAt: new Date(license.expiresAt).toISOString(),
        service: license.service,
        users: JSON.stringify(license.users),
        isDeviceRestrict: license.isDeviceRestrict,
        devices: JSON.stringify(devicesFormated),
        isModelRestrict: license.isModelRestrict,
        models: JSON.stringify(license.models),
        computerMac: license.computerMac
    };
}

export default function CardLicense({ 
    license, 
    onSave,
    showSnackBar 
} : CardLicenseProps) {
    const classes = useStyles();

    const accessToken = useRootSelector(
        (state) => state.user.currentToken?.accessToken
    );

    const [localLicense, setLocalLicense] = useState(license);

    const [isDialogOpened, setIsDialogOpened] = useState(false);
    const [isRenewDialogOpened, setIsRenewDialogOpened] = useState(false);
    const [isDetailsDialogOpened, setIsDetailsDialogOpened] = useState(false);
    
    const serviceToIcon = (service : SpectralService) => {
        switch(service){
            case SpectralService.Mobile:
                return <Smartphone />
            case SpectralService.VisionMachine:
                return <PhotoCamera />
            case SpectralService.Desktop:
                return <DesktopMac />
            default:
                return <Warning />
        }
    };

    const saveButtonHandleClick = async(licenseForm: LicenseForm) => {
        try {
            await updateLicense(
                localLicense.rowKey,
                toLicenseUpdateDTO(licenseForm),
                accessToken!);
            onSave();
            showSnackBar(intl.get("license.successfullyEdited"), true);
        } catch (err) {
            console.log(err);
            showSnackBar(intl.get("license.errorOnEdit"), false);
        }
        setIsDialogOpened(false);
    };

    const renewConfirmationHandle = async(newDate: Date) => {
        try {
            await updateLicense(
                localLicense.rowKey,
                {
                    name: localLicense.name,
                    description: localLicense.description,
                    price: localLicense.price,
                    expiresAt: newDate.toISOString(),
                    service: localLicense.service,
                    users: localLicense.users,
                    isDeviceRestrict: localLicense.isDeviceRestrict,
                    devices: localLicense.devices,
                    isModelRestrict: localLicense.isModelRestrict,
                    models: localLicense.models,
                    computerMac: localLicense.computerMac  
                },
                accessToken!
            );
            setLocalLicense(prev => ({ ...prev, expiresAt: newDate }));
            onSave();
            showSnackBar(intl.get("license.successfullyRenew"), true);
        } catch (err) {
            console.log(err);
            showSnackBar(intl.get("license.errorOnRenew"), false);
        }
        setIsRenewDialogOpened(false);
    }

    const licenseDownloadHandler = () => {
        const licenseContent = localLicense.validationToken

        let licenseName = ""

        switch(localLicense.service)
        {
            case SpectralService.VisionMachine:
                licenseName = "hsi.lic"
                break;
            case SpectralService.Desktop:
                licenseName = "soft.lic"
                break;
            default:
                throw new Error("Invalid license service");
        }

        const blob = new Blob([licenseContent], { type: "text/plain;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = licenseName;
        document.body.appendChild(a);
        a.click();

        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    const GetStyleByStatus = () => {
        switch (localLicense.status)
        {
            case LicenseStatus.Invalid:
                return classes.invalid;
            case LicenseStatus.Valid:
                return classes.valid;
            case LicenseStatus.Expired:
                return classes.expired;
            case LicenseStatus.ExpiringSoon:
                return classes.expiringSoon;
        }
    }

    const truncateText = (text: string, maxLenght = 45) => {
        if (!text) 
            return "";

        if (text.length > maxLenght)
            return text.substring(0, maxLenght) + "...";

        return text;
    }

    const getExpirationText = () => {
        if (license.status === LicenseStatus.Expired)
            return intl.get("license.expired");

        if (license.daysToExpire === 0)
            return intl.get("license.expiresToday");

        return `${intl.get("license.expiresAt")} ${license.daysToExpire} ${intl.get("license.days")}`;
    }

    return (
        <>
            <Card 
                key={localLicense.rowKey} 
                className={classes.card}>
                <CardHeader 
                    avatar={
                        <Avatar 
                            arial-label={intl.get("license.service")}
                            className={classes.avatar}>
                                {serviceToIcon(localLicense.service)}
                        </Avatar>   
                    }
                    title={
                        <div style={{ display: "flex", alignItems: "center" }}>
                            {
                                !localLicense.name ? (
                                    <>
                                        {localLicense.rowKey}
                                        <Tooltip title={intl.get("license.licenseIdentifier")}>
                                            <Info 
                                                fontSize="small"
                                                style={{ marginLeft: 8, cursor: "pointer", color: "#00acc1" }}
                                            />
                                        </Tooltip>
                                    </>
                                ) : (
                                    localLicense.name
                                )
                            }    
                        </div>
                    }
                    subheader={
                        !localLicense.description
                        ? intl.get("license.noDescription")
                        : (
                            <div style={{ maxHeight: 40, overflow: "hidden" }}>
                                <Tooltip title={localLicense.description}>
                                    <div className={classes.description}>
                                        {truncateText(localLicense.description)}
                                    </div>
                                </Tooltip>
                            </div>
                        )
                    }
                />
                <CardContent>
                    <div style={{ display: "flex", flexDirection: "column"}}>
                        <Chip
                            label={getExpirationText()}
                            className={GetStyleByStatus()}
                            variant="outlined"
                        />
                        <Link 
                            style={{ marginTop: 2 }}
                            component="button"
                            variant="body2"
                            onClick={() => setIsDetailsDialogOpened(!isDetailsDialogOpened)}>
                            Ver detalhes
                        </Link>
                    </div>
                </CardContent>
                <CardActions>
                    <Button
                        startIcon={<Edit />} 
                        onClick={() => setIsDialogOpened(!isDialogOpened)}>
                            {intl.get("license.edit")}
                    </Button>
                    <Button 
                        startIcon={<Replay />}
                        onClick={() => setIsRenewDialogOpened(!isRenewDialogOpened)} 
                        disabled={license.status === LicenseStatus.Valid}>
                            {intl.get("license.renew")}
                    </Button>

                    {localLicense.service != SpectralService.Mobile && (
                        <Button
                            startIcon={<GetAppRounded />}
                            onClick={licenseDownloadHandler}
                            disabled={license.status === LicenseStatus.Expired 
                                || license.status === LicenseStatus.Invalid
                            }>
                            {intl.get("license.download")}
                        </Button>
                    )}
                </CardActions>
            </Card>
            
            <LicenseDetailsDialog
                open={isDetailsDialogOpened}
                license={localLicense}
                onClose={() => setIsDetailsDialogOpened(false)}
            />

            <LicenseDialog
                open={isDialogOpened}
                license={localLicense}
                expiresAtDisabled={true}
                onClose={() => setIsDialogOpened(false)}
                onSave={saveButtonHandleClick}
                onCancel={() => setIsDialogOpened(false)}
            />

            <RenewLicenseDialog
                open={isRenewDialogOpened}
                onClose={() => setIsRenewDialogOpened(false)}
                onConfirm={renewConfirmationHandle}
            />
        </>
    );
}